import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';

import {
  IconArchive,
  IconChevronRight,
  IconClose,
  IconGlobe,
  IconLink,
  IconLock,
  IconLogOut,
  IconUsers,
} from '../components/icons';
import { LargeTitle } from '../components/layout';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyButton,
  Field,
  Input,
  List,
  Row,
  SectionHeader,
  Segmented,
  Select,
  Textarea,
  Sheet,
  Switch,
  useToast,
} from '../components/ui';
import { categoryName } from '../components/receipts';
import { CreateLedgerSheet, ShareSheet, SharedWithSummary } from '../components/ledgers';
import { useLedgerContext } from '../lib/ledger-context';
import { useI18n } from '../i18n';
import { LOCALES, type Locale } from '../i18n/types';
import { api, type Ledger, type Person, type Role, type Theme, type User } from '../lib/api';
import {
  keys,
  useAccessInvalidation,
  useCategories,
  useGroups,
  useInvalidate,
  usePeople,
  useSettings,
  useUpdateProfile,
  useUsers,
} from '../lib/queries';
import { ACCENT_TOKENS, accentVar } from '../lib/ui';
import { useErrorMessage } from './Auth';

const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'CNY', 'HKD', 'SGD', 'JPY'];

// --- Profile ----------------------------------------------------------------

function ProfileSection({
  user,
  theme,
  onThemeChange,
}: {
  user: User;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
}) {
  const { t, locale, setLocale } = useI18n();
  const toast = useToast();
  const updateProfile = useUpdateProfile();
  const [name, setName] = useState(user.name);

  const save = (patch: Record<string, unknown>) => {
    updateProfile.mutate(patch, { onSuccess: () => toast.show(t('common.saved'), 'positive') });
  };

  return (
    <section>
      <SectionHeader title={t('settings.profile')} />
      <Card className="space-y-4">
        <div className="flex items-center gap-3.5">
          <Avatar name={user.name} color={user.avatarColor} size={52} />
          <div className="min-w-0">
            <div className="truncate text-[16px] font-semibold text-[var(--ink)]">{user.name}</div>
            <div className="truncate text-[13px] text-[var(--ink-secondary)]">{user.email}</div>
          </div>
        </div>

        <Field label={t('settings.name')}>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => name.trim() && name !== user.name && save({ name: name.trim() })}
          />
        </Field>

        <Field label={t('settings.appearance')}>
          <Segmented
            className="w-full"
            value={theme}
            onChange={(next) => {
              onThemeChange(next);
              save({ theme: next });
            }}
            options={(['system', 'light', 'dark'] as Theme[]).map((value) => ({
              value,
              label: t(`settings.theme.${value}` as 'settings.theme.system'),
            }))}
          />
        </Field>

        <Field label={t('settings.language')}>
          <Segmented
            className="w-full"
            value={locale}
            onChange={(next: Locale) => {
              setLocale(next);
              save({ locale: next });
            }}
            options={LOCALES.map((item) => ({ value: item.value, label: item.nativeLabel }))}
          />
        </Field>

        <div>
          <div className="mb-2 text-[13px] font-medium text-[var(--ink-secondary)]">
            {t('a11y.memberColor', { name: user.name })}
          </div>
          <div className="flex flex-wrap gap-2">
            {ACCENT_TOKENS.map((token) => (
              <button
                key={token}
                type="button"
                aria-label={token}
                onClick={() => save({ avatarColor: token })}
                className="h-8 w-8 rounded-full transition-transform hover:scale-110"
                style={{
                  background: accentVar(token),
                  outline: user.avatarColor === token ? '2px solid var(--ink)' : 'none',
                  outlineOffset: 2,
                }}
              />
            ))}
          </div>
        </div>
      </Card>
    </section>
  );
}

// --- Security ---------------------------------------------------------------

function SecuritySection({ onSignOut }: { onSignOut: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');

  const change = useMutation({
    mutationFn: () => api.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast.show(t('auth.passwordChanged'), 'positive');
      setOpen(false);
      setCurrent('');
      setNext('');
    },
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const revokeOthers = useMutation({
    mutationFn: api.revokeOtherSessions,
    onSuccess: () => toast.show(t('settings.signOutOthersDone'), 'positive'),
  });

  return (
    <section>
      <SectionHeader title={t('settings.security')} />
      <List>
        <Row
          label={t('settings.changePassword')}
          leading={<IconLock size={19} className="text-[var(--ink-muted)]" />}
          trailing={<IconChevronRight size={18} className="text-[var(--ink-muted)]" />}
          onClick={() => setOpen(true)}
        />
        <Row
          label={t('settings.signOutOthers')}
          leading={<IconUsers size={19} className="text-[var(--ink-muted)]" />}
          onClick={() => revokeOthers.mutate()}
        />
        <Row
          label={t('auth.signOut')}
          leading={<IconLogOut size={19} className="text-[var(--critical)]" />}
          className="text-[var(--critical)]"
          onClick={onSignOut}
        />
      </List>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={t('settings.changePassword')}
        footer={
          <Button
            fullWidth
            size="lg"
            loading={change.isPending}
            disabled={next.length < 10}
            onClick={() => change.mutate()}
          >
            {t('common.save')}
          </Button>
        }
      >
        <div className="space-y-4 pt-1">
          <Field label={t('auth.currentPassword')}>
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>
          <Field label={t('auth.newPassword')} hint={t('auth.passwordTooShort')}>
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </Field>
        </div>
      </Sheet>
    </section>
  );
}

// --- Workspace ------------------------------------------------------------

function WorkspaceSection() {
  const { t } = useI18n();
  const toast = useToast();
  const settings = useSettings();
  const invalidate = useInvalidate(keys.settings);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.updateSettings(body),
    onSuccess: () => {
      void invalidate();
      toast.show(t('common.saved'), 'positive');
    },
  });

  const data = settings.data;
  if (!data) return null;

  const value = (key: keyof typeof data, fallback = '') =>
    draft[key] ?? (data[key] as string | null) ?? fallback;

  return (
    <section>
      <SectionHeader title={t('settings.workspace')} />
      <Card className="space-y-4">
        <Field label={t('settings.workspaceName')}>
          <Input
            value={value('workspaceName')}
            onChange={(event) => setDraft({ ...draft, workspaceName: event.target.value })}
            onBlur={() =>
              draft.workspaceName !== undefined &&
              save.mutate({ workspaceName: draft.workspaceName })
            }
          />
        </Field>

        <Field label={t('settings.currency')} hint={t('settings.currencyHelp')}>
          <Select
            value={value('currency', 'USD')}
            onChange={(event) => save.mutate({ currency: event.target.value })}
          >
            {CURRENCIES.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('settings.publicUrl')} hint={t('settings.publicUrlHelp')} optional>
          <Input
            type="url"
            placeholder={window.location.origin}
            value={value('publicUrl')}
            onChange={(event) => setDraft({ ...draft, publicUrl: event.target.value })}
            onBlur={() =>
              draft.publicUrl !== undefined && save.mutate({ publicUrl: draft.publicUrl })
            }
          />
        </Field>
      </Card>
    </section>
  );
}

// --- Ledgers --------------------------------------------------------------

function LedgersSection({ user }: { user: User }) {
  const { t } = useI18n();
  const { ledgers } = useLedgerContext();
  const [creating, setCreating] = useState(false);
  const [sharing, setSharing] = useState<Ledger | null>(null);
  const [editing, setEditing] = useState<Ledger | null>(null);

  return (
    <section>
      <SectionHeader
        title={t('settings.ledgers')}
        description={t('settings.ledgersHelp')}
        action={
          <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
            {t('ledger.create')}
          </Button>
        }
      />

      {ledgers.length === 0 ? (
        <Card>
          <p className="text-[14px] text-[var(--ink)]">{t('ledger.emptyTitle')}</p>
          <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">{t('ledger.emptyBody')}</p>
        </Card>
      ) : (
        <div className="card divide-y divide-[var(--line)] overflow-hidden">
          {/*
            Name, status and two buttons do not fit on one line of a phone: the
            name was being squeezed to 22px of a 288px row and truncated to
            nothing. The actions drop below the name until there is room beside
            it.
          */}
          {ledgers.map((ledger) => (
            <div
              key={ledger.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 sm:flex-nowrap"
            >
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-full"
                style={{ background: accentVar(ledger.color) }}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 basis-[60%]">
                <span className="block truncate text-[15px] text-[var(--ink)]">{ledger.name}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] leading-snug text-[var(--ink-secondary)]">
                  <span>{t('ledger.receiptCount', { count: ledger.receiptCount })}</span>
                  {ledger.access === 'manage' ? (
                    <SharedWithSummary count={ledger.peopleCount} />
                  ) : (
                    <Badge>{t(`access.${ledger.access}` as 'access.view')}</Badge>
                  )}
                </span>
              </span>
              {ledger.access === 'manage' ? (
                <div className="flex shrink-0 gap-1.5 max-sm:ml-auto">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(ledger)}>
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setSharing(ledger)}>
                    {t('access.share')}
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <CreateLedgerSheet open={creating} onClose={() => setCreating(false)} />
      {sharing ? (
        <ShareSheet
          open
          onClose={() => setSharing(null)}
          ledger={sharing}
          currentUserId={user.id}
        />
      ) : null}
      {editing ? <LedgerSettingsSheet ledger={editing} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

function LedgerSettingsSheet({ ledger, onClose }: { ledger: Ledger; onClose: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const invalidate = useAccessInvalidation();
  const [name, setName] = useState(ledger.name);
  const [currency, setCurrency] = useState(ledger.currency);
  const [openedOn, setOpenedOn] = useState(ledger.hsaEstablishedDate ?? '');
  const [confirmArchive, setConfirmArchive] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.updateLedger(ledger.id, {
        name: name.trim(),
        currency,
        hsaEstablishedDate: openedOn || null,
      }),
    onSuccess: () => {
      invalidate();
      toast.show(t('common.saved'), 'positive');
      onClose();
    },
  });

  const archive = useMutation({
    mutationFn: () => api.archiveLedger(ledger.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  return (
    <>
      <Sheet
        open
        onClose={onClose}
        title={t('ledger.settings')}
        footer={
          <Button fullWidth size="lg" loading={save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        }
      >
        <div className="space-y-4 pt-1">
          <Field label={t('ledger.name')}>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>

          <Field label={t('ledger.currency')}>
            <Select value={currency} onChange={(event) => setCurrency(event.target.value)}>
              {CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('ledger.opened')} hint={t('ledger.openedHelp')} optional>
            <Input
              type="date"
              value={openedOn}
              onChange={(event) => setOpenedOn(event.target.value)}
            />
          </Field>

          <PeopleSection ledgerId={ledger.id} />

          <Button
            variant="ghost"
            fullWidth
            className="text-[var(--critical)]"
            onClick={() => setConfirmArchive(true)}
          >
            {t('ledger.archive')}
          </Button>
        </div>
      </Sheet>

      <ConfirmDialog
        open={confirmArchive}
        title={t('ledger.archiveConfirmTitle')}
        body={t('ledger.archiveConfirmBody')}
        confirmLabel={t('ledger.archive')}
        destructive
        onCancel={() => setConfirmArchive(false)}
        onConfirm={() => archive.mutate()}
      />
    </>
  );
}

// --- People (within a ledger) ---------------------------------------------

function PeopleSection({ ledgerId }: { ledgerId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const people = usePeople(ledgerId);
  const invalidate = useInvalidate(keys.people(ledgerId));
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('child');

  const [removing, setRemoving] = useState<Person | null>(null);

  const create = useMutation({
    mutationFn: () => api.createPerson(ledgerId, { name: name.trim(), relationship }),
    onSuccess: () => {
      void invalidate();
      setName('');
      toast.show(t('common.saved'), 'positive');
    },
  });

  const remove = useMutation({
    mutationFn: (person: Person) => api.deletePerson(ledgerId, person.id),
    onSuccess: () => {
      void invalidate();
      setRemoving(null);
      toast.show(t('settings.personRemoved'), 'positive');
    },
  });

  return (
    <>
      <div>
        <div className="mb-1.5 text-[13px] font-medium text-[var(--ink-secondary)]">
          {t('settings.people')}
        </div>
        <p className="mb-2.5 text-[12px] leading-snug text-[var(--ink-muted)]">
          {t('settings.peopleHelp')}
        </p>

        {(people.data?.people.length ?? 0) > 0 ? (
          <ul className="mb-3 flex flex-wrap gap-2">
            {people.data?.people.map((person) => (
              <li
                key={person.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] py-1 pl-1 pr-1 text-[13px]"
              >
                <Avatar name={person.name} color={person.color} size={22} />
                <span className="max-w-[160px] truncate">{person.name}</span>
                <button
                  type="button"
                  onClick={() => setRemoving(person)}
                  aria-label={t('settings.removePerson', { name: person.name })}
                  className="press rounded-full p-1 text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)] hover:text-[var(--critical)]"
                >
                  <IconClose size={14} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex gap-2">
          <Input
            value={name}
            placeholder={t('settings.personName')}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && name.trim()) {
                event.preventDefault();
                create.mutate();
              }
            }}
          />
          <Select
            value={relationship}
            onChange={(event) => setRelationship(event.target.value)}
            className="w-auto"
          >
            {(['self', 'spouse', 'child', 'parent', 'dependent', 'other'] as const).map(
              (option) => (
                <option key={option} value={option}>
                  {t(`settings.relationships.${option}` as 'settings.relationships.self')}
                </option>
              ),
            )}
          </Select>
          <Button
            variant="secondary"
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {t('common.add')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={removing !== null}
        title={t('settings.removePersonTitle', { name: removing?.name ?? '' })}
        body={t('settings.removePersonBody')}
        confirmLabel={t('common.remove')}
        destructive
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
      />
    </>
  );
}
function GroupsSection() {
  const { t } = useI18n();
  const toast = useToast();
  const groups = useGroups();
  const users = useUsers();
  const invalidate = useAccessInvalidation();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createGroup({ name: name.trim() }),
    onSuccess: () => {
      invalidate();
      setName('');
      setCreating(false);
      toast.show(t('common.saved'), 'positive');
    },
  });

  const detail = useQuery({
    queryKey: keys.group(editing ?? ''),
    queryFn: () => api.group(editing!),
    enabled: Boolean(editing),
  });

  const toggleMember = useMutation({
    mutationFn: async ({ userId, member }: { userId: string; member: boolean }) => {
      if (member) await api.removeGroupMember(editing!, userId);
      else await api.addGroupMember(editing!, userId);
    },
    onSuccess: () => {
      void detail.refetch();
      invalidate();
    },
  });

  const memberIds = new Set((detail.data?.members ?? []).map((m) => m.id));

  return (
    <section>
      <SectionHeader
        title={t('groups.title')}
        description={t('groups.subtitle')}
        action={
          <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
            {t('groups.create')}
          </Button>
        }
      />

      {(groups.data?.groups.length ?? 0) === 0 ? (
        <Card>
          <p className="text-[14px] text-[var(--ink)]">{t('groups.empty')}</p>
          <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">{t('groups.emptyHelp')}</p>
        </Card>
      ) : (
        <List>
          {groups.data?.groups.map((group) => (
            <Row
              key={group.id}
              leading={
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[var(--ink-secondary)]">
                  <IconUsers size={16} />
                </span>
              }
              label={group.name}
              value={t('groups.memberCount', { count: group.memberCount })}
              trailing={<IconChevronRight size={18} className="text-[var(--ink-muted)]" />}
              onClick={() => setEditing(group.id)}
            />
          ))}
        </List>
      )}

      <Sheet
        open={creating}
        onClose={() => setCreating(false)}
        title={t('groups.create')}
        footer={
          <Button
            fullWidth
            size="lg"
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {t('common.add')}
          </Button>
        }
      >
        <Field label={t('groups.name')}>
          <Input
            autoFocus
            value={name}
            placeholder={t('groups.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
      </Sheet>

      <Sheet
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={detail.data?.group.name ?? t('groups.title')}
      >
        <div className="pt-1">
          <h3 className="mb-2 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('groups.members')}
          </h3>
          <div className="divide-y divide-[var(--line)]">
            {users.data?.users.map((candidate) => {
              const isMember = memberIds.has(candidate.id);
              return (
                <div key={candidate.id} className="flex items-center gap-3 py-2.5">
                  <Avatar name={candidate.name} color={candidate.avatarColor} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] text-[var(--ink)]">{candidate.name}</div>
                    <div className="truncate text-[12px] text-[var(--ink-secondary)]">
                      {candidate.email}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={isMember ? 'ghost' : 'secondary'}
                    onClick={() => toggleMember.mutate({ userId: candidate.id, member: isMember })}
                  >
                    {isMember ? t('common.remove') : t('common.add')}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </Sheet>
    </section>
  );
}

// --- Users ------------------------------------------------------------------

function UsersSection({ currentUser }: { currentUser: User }) {
  const { t, date } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const users = useUsers();
  const invalidate = useInvalidate(keys.users);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({
    email: '',
    name: '',
    role: 'member' as Role,
    sendEmail: true,
  });
  const [inviteLink, setInviteLink] = useState<{ link: string; emailed: boolean } | null>(null);
  const [removing, setRemoving] = useState<User | null>(null);

  const invite = useMutation({
    mutationFn: () => api.invite(form),
    onSuccess: (result) => {
      void invalidate();
      setInviteLink({ link: result.link, emailed: result.emailed });
      if (result.emailed) toast.show(t('users.inviteSent', { email: form.email }), 'positive');
    },
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.updateUser(id, body),
    onSuccess: () => void invalidate(),
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const removeUser = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      void invalidate();
      setRemoving(null);
    },
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const resetLink = useMutation({
    mutationFn: (id: string) => api.userResetLink(id),
    onSuccess: (result) => setInviteLink({ link: result.link, emailed: result.emailed }),
  });

  const emailConfigured = users.data?.emailConfigured ?? false;

  return (
    <section>
      <SectionHeader
        title={t('settings.users')}
        description={t('settings.usersHelp')}
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setInviteLink(null);
              setForm({ email: '', name: '', role: 'member', sendEmail: emailConfigured });
              setInviteOpen(true);
            }}
          >
            {t('settings.invitePerson')}
          </Button>
        }
      />

      <List>
        {users.data?.users.map((user) => (
          <Row
            key={user.id}
            leading={<Avatar name={user.name} color={user.avatarColor} size={34} />}
            label={user.name}
            hint={user.email}
            value={
              <span className="flex items-center gap-2">
                {user.status === 'disabled' ? (
                  <Badge tone="critical">{t('users.disabled')}</Badge>
                ) : user.role === 'admin' ? (
                  <Badge tone="accent">{t('users.admin')}</Badge>
                ) : null}
              </span>
            }
            onClick={
              user.id === currentUser.id
                ? undefined
                : () => {
                    const nextRole = user.role === 'admin' ? 'member' : 'admin';
                    updateUser.mutate({ id: user.id, body: { role: nextRole } });
                  }
            }
          />
        ))}
      </List>

      {users.data?.users.some((user) => user.id !== currentUser.id) ? (
        <div className="mt-2 flex flex-wrap gap-2 px-1">
          {users.data.users
            .filter((user) => user.id !== currentUser.id)
            .map((user) => (
              <div key={user.id} className="flex items-center gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => resetLink.mutate(user.id)}>
                  {t('users.resetPassword')}: {user.name}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRemoving(user)}>
                  {t('users.removeUser')}
                </Button>
              </div>
            ))}
        </div>
      ) : null}

      {(users.data?.invitations.length ?? 0) > 0 ? (
        <div className="mt-4">
          <SectionHeader title={t('users.pendingInvites')} />
          <List>
            {users.data?.invitations.map((invitation) => (
              <Row
                key={invitation.id}
                label={invitation.name}
                hint={`${invitation.email} · ${t('users.expires', { date: date(invitation.expiresAt) })}`}
                trailing={
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        api
                          .resendInvitation(invitation.id)
                          .then((result) => {
                            void invalidate();
                            setInviteLink({ link: result.link, emailed: result.emailed });
                          })
                          .catch(() => toast.show(t('errors.generic'), 'critical'))
                      }
                    >
                      {t('users.resend')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        api.revokeInvitation(invitation.id).then(() => void invalidate())
                      }
                    >
                      {t('users.revoke')}
                    </Button>
                  </div>
                }
              />
            ))}
          </List>
        </div>
      ) : null}

      <Sheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={t('users.inviteTitle')}
        footer={
          inviteLink ? (
            <Button fullWidth size="lg" onClick={() => setInviteOpen(false)}>
              {t('common.done')}
            </Button>
          ) : (
            <Button
              fullWidth
              size="lg"
              loading={invite.isPending}
              disabled={!form.email.trim() || !form.name.trim()}
              onClick={() => invite.mutate()}
            >
              {t('settings.invitePerson')}
            </Button>
          )
        }
      >
        {inviteLink ? (
          <div className="pt-1">
            <p className="text-[14px] leading-relaxed text-[var(--ink)]">
              {inviteLink.emailed
                ? t('users.inviteSent', { email: form.email })
                : t('users.inviteLinkReady')}
            </p>
            <p className="mt-1.5 text-[13px] text-[var(--ink-secondary)]">
              {t('users.inviteLinkHelp')}
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-3">
              <code className="min-w-0 flex-1 truncate text-[12px] text-[var(--ink-secondary)]">
                {inviteLink.link}
              </code>
              <CopyButton value={inviteLink.link} />
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <p className="text-[14px] leading-relaxed text-[var(--ink-secondary)]">
              {t('users.inviteBody')}
            </p>

            <Field label={t('users.inviteName')}>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>

            <Field label={t('users.inviteEmail')}>
              <Input
                type="email"
                autoCapitalize="none"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>

            <Field label={t('users.role')} hint={t(`users.${form.role}Help` as 'users.adminHelp')}>
              <Segmented
                className="w-full"
                value={form.role}
                onChange={(role: Role) => setForm({ ...form, role })}
                options={[
                  { value: 'member' as Role, label: t('users.member') },
                  { value: 'admin' as Role, label: t('users.admin') },
                ]}
              />
            </Field>

            {emailConfigured ? (
              <div className="flex items-center justify-between gap-4">
                <span className="text-[14px] text-[var(--ink)]">{t('users.sendEmail')}</span>
                <Switch
                  checked={form.sendEmail}
                  onChange={(sendEmail) => setForm({ ...form, sendEmail })}
                  label={t('users.sendEmail')}
                />
              </div>
            ) : (
              <p className="text-[13px] leading-snug text-[var(--ink-muted)]">
                {t('users.sendEmailUnavailable')}
              </p>
            )}
          </div>
        )}
      </Sheet>

      <ConfirmDialog
        open={Boolean(removing)}
        title={t('users.removeConfirmTitle', { name: removing?.name ?? '' })}
        body={t('users.removeConfirmBody')}
        confirmLabel={t('users.removeUser')}
        destructive
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && removeUser.mutate(removing.id)}
      />
    </section>
  );
}

// --- Paperless --------------------------------------------------------------

function PaperlessCard() {
  const { t } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const settings = useSettings();
  const invalidate = useInvalidate(keys.settings);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'password' | 'token'>('password');
  const [form, setForm] = useState({ url: '', username: '', password: '', token: '' });
  const [info, setInfo] = useState<{ documentCount: number; version: string | null } | null>(null);

  const connect = useMutation({
    mutationFn: async () => {
      if (mode === 'token') {
        await api.updateSettings({ paperlessUrl: form.url, paperlessToken: form.token });
        return api.testPaperless({});
      }
      return api.connectPaperless({
        url: form.url,
        username: form.username,
        password: form.password,
      });
    },
    onSuccess: (result) => {
      void invalidate();
      setInfo(result);
      toast.show(t('paperless.testOk'), 'positive');
    },
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const test = useMutation({
    mutationFn: () => api.testPaperless({}),
    onSuccess: (result) => {
      setInfo(result);
      toast.show(t('paperless.testOk'), 'positive');
    },
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const connected = settings.data?.paperless.token.configured ?? false;
  const url = settings.data?.paperless.url ?? '';

  return (
    <>
      <Card>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-[var(--ink-muted)]">
            <IconLink size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-medium text-[var(--ink)]">
                {t('paperless.title')}
              </span>
              {connected ? (
                <Badge tone="positive">{t('paperless.connected')}</Badge>
              ) : (
                <Badge tone="caution">{t('paperless.notConnected')}</Badge>
              )}
            </div>
            <p className="mt-1 text-[13px] leading-snug text-[var(--ink-secondary)]">
              {connected ? t('paperless.connectedTo', { url }) : t('paperless.subtitle')}
            </p>
            {info ? (
              <p className="mt-1 text-[13px] text-[var(--ink-muted)]">
                {t('paperless.documentCount', { count: info.documentCount })}
              </p>
            ) : null}

            <div className="mt-3.5 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setForm({ ...form, url: url || 'http://paperless:8000' });
                  setOpen(true);
                }}
              >
                {connected ? t('common.edit') : t('paperless.connect')}
              </Button>
              {connected ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={test.isPending}
                  onClick={() => test.mutate()}
                >
                  {t('paperless.test')}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </Card>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={t('paperless.title')}
        footer={
          <Button
            fullWidth
            size="lg"
            loading={connect.isPending}
            disabled={!form.url}
            onClick={() => connect.mutate()}
          >
            {connect.isPending ? t('paperless.connecting') : t('paperless.connect')}
          </Button>
        }
      >
        <div className="space-y-4 pt-1">
          <p className="text-[14px] leading-relaxed text-[var(--ink-secondary)]">
            {t('paperless.subtitle')}
          </p>

          <Field label={t('paperless.serverUrl')}>
            <Input
              type="url"
              autoCapitalize="none"
              placeholder={t('paperless.serverUrlPlaceholder')}
              value={form.url}
              onChange={(event) => setForm({ ...form, url: event.target.value })}
            />
          </Field>

          <Segmented
            className="w-full"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'password' as const, label: t('paperless.usePassword') },
              { value: 'token' as const, label: t('paperless.useToken') },
            ]}
          />

          {mode === 'password' ? (
            <>
              <Field label={t('paperless.username')}>
                <Input
                  autoCapitalize="none"
                  value={form.username}
                  onChange={(event) => setForm({ ...form, username: event.target.value })}
                />
              </Field>
              <Field label={t('paperless.passwordLabel')} hint={t('paperless.passwordHelp')}>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                />
              </Field>
            </>
          ) : (
            <Field label={t('paperless.apiToken')}>
              <Input
                autoCapitalize="none"
                value={form.token}
                onChange={(event) => setForm({ ...form, token: event.target.value })}
              />
            </Field>
          )}
        </div>
      </Sheet>
    </>
  );
}

// --- Google Sheets backup ---------------------------------------------------

const FREQUENCIES = [
  { value: 'entry', label: 'sheets.frequencyEntry' },
  { value: 'hourly', label: 'sheets.frequencyHourly' },
  { value: 'daily', label: 'sheets.frequencyDaily' },
  { value: 'weekly', label: 'sheets.frequencyWeekly' },
  { value: 'manual', label: 'sheets.frequencyManual' },
] as const;

/**
 * The off-site copy.
 *
 * Everything else in Cairn assumes this server still exists. This is the part
 * that does not, so the card leads with what the backup is *for* rather than
 * with how to configure it, and the setup itself is tucked into a sheet.
 */
function SheetsCard() {
  const { t, date } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const settings = useSettings();
  const invalidate = useInvalidate(keys.settings);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ spreadsheetId: '', serviceAccount: '' });

  const sheets = settings.data?.sheets ?? null;
  const enabled = sheets?.enabled ?? false;
  const configured = Boolean(sheets?.serviceAccount.configured && sheets?.spreadsheetId);

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateSettings({ sheets: patch }),
    onSuccess: () => void invalidate(),
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const connect = useMutation({
    mutationFn: async () => {
      const result = await api.testSheets({
        spreadsheetId: form.spreadsheetId || undefined,
        serviceAccount: form.serviceAccount || undefined,
      });
      // Only saved once Google has confirmed it works, so a typo never becomes
      // a backup that silently never runs.
      await api.updateSettings({
        sheets: {
          spreadsheetId: form.spreadsheetId || undefined,
          serviceAccount: form.serviceAccount || undefined,
          enabled: true,
        },
      });
      return result;
    },
    onSuccess: (result) => {
      void invalidate();
      setForm({ spreadsheetId: '', serviceAccount: '' });
      setOpen(false);
      toast.show(t('sheets.testOk', { title: result.title }), 'positive');
    },
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  const sync = useMutation({
    mutationFn: () => api.syncSheets(),
    onSuccess: (result) => {
      void invalidate();
      if (result.status === 'ok') {
        toast.show(t('sheets.syncOk', { rows: result.rows, ledgers: result.ledgers }), 'positive');
      } else if (result.status === 'skipped') {
        toast.show(t('sheets.syncNothing'), 'neutral');
      } else {
        toast.show(result.reason ?? t('common.somethingWentWrong'), 'critical');
      }
    },
    onError: (error) => toast.show(toMessage(error), 'critical'),
  });

  return (
    <>
      <Card className="mt-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-[var(--ink-muted)]">
            <IconArchive size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] font-medium text-[var(--ink)]">{t('sheets.title')}</span>
              {configured && enabled ? (
                <Badge tone="positive">{t('sheets.connected')}</Badge>
              ) : configured ? (
                <Badge tone="neutral">{t('sheets.off')}</Badge>
              ) : (
                <Badge tone="caution">{t('sheets.notConnected')}</Badge>
              )}
            </div>
            <p className="mt-1 text-[13px] leading-snug text-[var(--ink-secondary)]">
              {t('sheets.subtitle')}
            </p>

            {configured ? (
              <>
                <p className="mt-1.5 break-words text-[13px] text-[var(--ink-muted)]">
                  {sheets?.lastSyncedAt
                    ? t('sheets.lastSynced', { when: date(sheets.lastSyncedAt, 'short') })
                    : t('sheets.neverSynced')}
                </p>
                {sheets?.lastError ? (
                  <p className="mt-1 break-words text-[13px] text-[var(--critical)]">
                    {t('sheets.lastError', { message: sheets.lastError })}
                  </p>
                ) : null}

                <div className="mt-3.5 flex items-center justify-between gap-3">
                  <span className="min-w-0 text-[14px] text-[var(--ink)]">
                    {t('sheets.enable')}
                  </span>
                  <Switch
                    checked={enabled}
                    label={t('sheets.enable')}
                    onChange={(next) => save.mutate({ enabled: next })}
                  />
                </div>

                <div className="mt-3">
                  <Field label={t('sheets.frequency')} hint={t('sheets.tabsNote')}>
                    <Select
                      value={sheets?.frequency ?? 'entry'}
                      onChange={(event) => save.mutate({ frequency: event.target.value })}
                    >
                      {FREQUENCIES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(option.label)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </>
            ) : null}

            <div className="mt-3.5 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setForm({ spreadsheetId: sheets?.spreadsheetId ?? '', serviceAccount: '' });
                  setOpen(true);
                }}
              >
                {configured ? t('common.edit') : t('sheets.setUp')}
              </Button>
              {configured ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={sync.isPending}
                  onClick={() => sync.mutate()}
                >
                  {sync.isPending ? t('sheets.syncing') : t('sheets.syncNow')}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </Card>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={t('sheets.setupTitle')}
        footer={
          <Button
            fullWidth
            size="lg"
            loading={connect.isPending}
            disabled={!form.spreadsheetId || (!form.serviceAccount && !configured)}
            onClick={() => connect.mutate()}
          >
            {connect.isPending ? t('sheets.syncing') : t('sheets.test')}
          </Button>
        }
      >
        <div className="space-y-4 pt-1">
          <p className="text-[14px] leading-relaxed text-[var(--ink-secondary)]">
            {t('sheets.setupSteps')}
          </p>

          <Field label={t('sheets.spreadsheet')} hint={t('sheets.spreadsheetHelp')}>
            <Input
              autoCapitalize="none"
              placeholder={t('sheets.spreadsheetPlaceholder')}
              value={form.spreadsheetId}
              onChange={(event) => setForm({ ...form, spreadsheetId: event.target.value })}
            />
          </Field>

          <Field
            label={t('sheets.serviceAccount')}
            hint={
              sheets?.serviceAccount.email
                ? t('sheets.serviceAccountSaved', { email: sheets.serviceAccount.email })
                : t('sheets.serviceAccountHelp')
            }
          >
            <Textarea
              rows={5}
              autoCapitalize="none"
              spellCheck={false}
              className="font-mono text-[12px]"
              placeholder={'{ "type": "service_account", … }'}
              value={form.serviceAccount}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                setForm({ ...form, serviceAccount: event.target.value })
              }
            />
          </Field>

          {sheets?.serviceAccount.email ? (
            <div className="rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-3">
              <p className="text-[13px] leading-snug text-[var(--ink-secondary)]">
                {t('sheets.shareReminder', { email: sheets.serviceAccount.email })}
              </p>
              <div className="mt-2">
                <CopyButton value={sheets.serviceAccount.email} />
              </div>
            </div>
          ) : null}
        </div>
      </Sheet>
    </>
  );
}

function ConnectionsSection() {
  const { t } = useI18n();
  return (
    <section>
      <SectionHeader title={t('settings.connections')} />
      <PaperlessCard />
      <SheetsCard />
    </section>
  );
}

// --- Categories -------------------------------------------------------------

function CategoriesSection() {
  const { t, locale } = useI18n();
  const categories = useCategories();

  return (
    <section>
      <SectionHeader title={t('settings.categories')} description={t('settings.categoriesHelp')} />
      <List>
        {categories.data?.categories.map((category) => (
          <Row
            key={category.id}
            leading={
              <span
                className="h-3 w-3 rounded-full"
                style={{ background: accentVar(category.color) }}
                aria-hidden="true"
              />
            }
            label={categoryName(category, locale)}
            value={locale === 'zh-CN' ? category.nameEn : category.nameZh}
          />
        ))}
      </List>
    </section>
  );
}

// --- Screen -----------------------------------------------------------------

export function SettingsScreen({
  user,
  theme,
  onThemeChange,
  onSignOut,
  version,
}: {
  user: User;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
  onSignOut: () => void;
  version: string;
}) {
  const { t } = useI18n();
  const isAdmin = user.role === 'admin';

  return (
    <>
      <LargeTitle title={t('settings.title')} />

      <div className="space-y-7 px-4 pt-3 lg:px-8">
        <ProfileSection user={user} theme={theme} onThemeChange={onThemeChange} />
        <SecuritySection onSignOut={onSignOut} />

        <LedgersSection user={user} />

        {isAdmin ? (
          <>
            <WorkspaceSection />
            <GroupsSection />
            <UsersSection currentUser={user} />
            <ConnectionsSection />
            <CategoriesSection />
          </>
        ) : null}

        <section>
          <SectionHeader title={t('settings.about')} />
          <List>
            <Row
              label={t('settings.version')}
              value={version}
              leading={<IconGlobe size={19} className="text-[var(--ink-muted)]" />}
            />
          </List>
          <p className="mt-3 px-1 text-[12px] leading-relaxed text-[var(--ink-muted)]">
            {t('brand.about')}
          </p>
        </section>
      </div>
    </>
  );
}
